import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        initializeAuth();
        authService.setOnUnauthorized(() => {
            setUser(null);
            setIsAuthenticated(false);
        });
        return () => authService.setOnUnauthorized(null);
    }, []);

    const initializeAuth = async () => {
        setLoading(true);
        
        if (authService.isAuthenticated()) {
            try {
                const result = await authService.getCurrentUser();
                if (result.success) {
                    setUser(result.user);
                    setIsAuthenticated(true);
                } else {
                    // Only clear session on 401 (invalid/expired token), not on 429 (rate limit) or other errors
                    if (result.status === 401) {
                        authService.clearSession();
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                    // On 429 or other failure, keep current auth state so user isn't logged out by rate limit
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                // Only clear session on 401 Unauthorized; 429 and network errors don't force logout
                const status = error.response?.status;
                if (status === 401) {
                    authService.clearSession();
                    setUser(null);
                    setIsAuthenticated(false);
                }
            }
        }
        
        setLoading(false);
    };

    const login = async (credentials) => {
        setLoading(true);
        try {
            const result = await authService.login(credentials);
            if (result.success) {
                setUser(result.user);
                setIsAuthenticated(true);
                return { success: true };
            }
            // Pass MFA challenge info back to the UI without marking it as a hard error.
            if (result.mfa_required) {
                return {
                    success: false,
                    mfa_required: true,
                    mfa_challenge_token: result.mfa_challenge_token,
                    email_hint: result.email_hint,
                    message: result.message,
                    dev_otp: result.dev_otp || null,
                };
            }
            return { success: false, error: result.error };
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const verifyMfa = async (challengeToken, otp) => {
        setLoading(true);
        try {
            const result = await authService.verifyMfa(challengeToken, otp);
            if (result.success) {
                setUser(result.user);
                setIsAuthenticated(true);
                return { success: true };
            }
            return { success: false, error: result.error };
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    const hasPermission = (permission) => {
        return authService.hasPermission(permission);
    };

    const isAdmin = () => {
        return authService.isAdmin();
    };

    const isViewer = () => {
        return authService.isViewer();
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        verifyMfa,
        logout,
        hasPermission,
        isAdmin,
        isViewer
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};